/** Phone, WhatsApp and Add Listing clicks, tracked from one delegated listener.
 *
 * Why not edit the components: `tel:` appears 39 times across 22 files, WhatsApp
 * in another handful and Add Listing in 15 — and every one added later would be
 * missed. One listener on `document` catches all of them, today and tomorrow,
 * and follows the pattern `GlobalLinkInterceptor` already uses here.
 *
 * Capture phase, because React handlers on these links may stop propagation
 * before a bubbling listener would ever see the click.
 */
import { useEffect } from "react";
import {
  DATA_LAYER_EVENTS,
  trackEngagementClick,
  type DataLayerEvent,
} from "~/lib/ConversionTracking";

/** Same link clicked twice within this window counts once — a double click on a
 * phone number is one intent, not two. */
const DEDUPE_MS = 1000;

/** Exported so the decision table can be asserted directly — the rules are the
 * whole point of this file, and a transcription of them into a test is not the
 * same thing as the rules themselves. */
export function classifyLinkClick(href: string): DataLayerEvent | null {
  if (href.startsWith("tel:")) return DATA_LAYER_EVENTS.PHONE_CLICK;
  if (/^https?:\/\/(wa\.me|(api|web|chat)\.whatsapp\.com)/i.test(href)) {
    return DATA_LAYER_EVENTS.WHATSAPP_CLICK;
  }
  // Matches /add-listing, /in/add-listing, and any query string on the end.
  if (/\/add-listing(\/|\?|#|$)/.test(href)) return DATA_LAYER_EVENTS.ADD_LISTING_CLICK;
  return null;
}

export function useClickTracking(enabled: boolean): void {
  useEffect(() => {
    if (!enabled || typeof document === "undefined") return;

    let lastHref = "";
    let lastAt = 0;

    const onClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const link = target?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!link) return;

      const href = link.getAttribute("href") || "";
      const name = classifyLinkClick(href);
      if (!name) return;

      const now = Date.now();
      if (href === lastHref && now - lastAt < DEDUPE_MS) return;
      lastHref = href;
      lastAt = now;

      trackEngagementClick(name, {
        link_url: href,
        link_text: (link.textContent || "").replace(/\s+/g, " ").trim().slice(0, 80),
      });
    };

    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [enabled]);
}
